import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

export type ButtonShape = 'rectangular' | 'rounded' | 'pill';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  /**
   * The variant to use.
   * @default 'contained'
   */
  variant?: 'text' | 'outlined' | 'contained';
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * The shape of the button border.
   * - `rectangular`: sharp corners (no radius)
   * - `rounded`: slightly rounded corners
   * - `pill`: fully rounded ends (oblong / pill shaped)
   * @default 'rectangular'
   */
  shape?: ButtonShape;
  /**
   * When set, renders as an `<a>` instead of a `<button>`.
   */
  href?: string;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: string;
  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * If `true`, the button will be disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Element placed before the children.
   */
  startIcon?: ReactNode;
  /**
   * Element placed after the children.
   */
  endIcon?: ReactNode;
  /**
   * The content of the button.
   */
  children: ReactNode;
}

const shapeBorderRadius: Record<ButtonShape, string> = {
  rectangular: '0px',
  rounded: '4px',
  pill: '9999px',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'contained',
      color = 'primary',
      size = 'medium',
      shape = 'rectangular',
      fullWidth = false,
      disabled = false,
      startIcon,
      endIcon,
      children,
      sx,
      ...props
    },
    ref
  ) => (
    <MuiButton
      ref={ref}
      variant={variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      startIcon={startIcon}
      endIcon={endIcon}
      sx={{
        typography: 'button',
        borderRadius: shapeBorderRadius[shape],
        py: 'calc(0.75rem + 1.5px)',
        ...(shape === 'pill' && {
          minWidth: 115,
          height: 48,
          py: 0,
        }),
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiButton>
  )
);

Button.displayName = 'Button';
